import React from "react";
import { Loader2 } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "./ui/accordion";
import { FAQ as FAQ_TID } from "../constants/testIds";
import { useContent } from "../hooks/useContent";

// Rend cliquables les URL présentes dans une réponse FAQ (texte brut en base),
// ex. les liens Instagram / TikTok. Ouverture dans un nouvel onglet.
const URL_RE = /(https?:\/\/[^\s]+)/g;
const URL_TEST = /^https?:\/\//;

const renderAnswer = (text) => {
    const parts = String(text).split(URL_RE);
    return parts.map((part, i) =>
        URL_TEST.test(part) ? (
            <a
                key={i}
                href={part}
                target="_blank"
                rel="noopener"
                className="font-medium text-[#0A84FF] underline underline-offset-2 hover:text-[#d4af37] transition-colors"
            >
                {part}
            </a>
        ) : (
            <React.Fragment key={i}>{part}</React.Fragment>
        ),
    );
};

const FALLBACK_FAQ = [
    {
        question: "Comment passer commande ?",
        answer:
            "Ajoutez vos articles au panier, puis demandez un devis via WhatsApp. Notre équipe vous confirme la disponibilité, la compatibilité et les frais de livraison avant validation.",
    },
    {
        question: "Quels sont les délais de livraison ?",
        answer:
            "La plupart des commandes sont préparées sous 24 h ouvrées et livrées entre 1 et 3 jours selon votre ville. Un lien de suivi vous est envoyé dès l'expédition.",
    },
    {
        question: "Les accessoires sont-ils compatibles avec mon appareil ?",
        answer:
            "Chaque fiche produit indique les modèles compatibles. Vous pouvez aussi nous envoyer votre référence par WhatsApp pour une vérification rapide avant achat.",
    },
    {
        question: "Proposez-vous une garantie ou un SAV ?",
        answer:
            "Oui, tous nos produits bénéficient de la garantie constructeur. En cas de problème, notre service après-vente vous accompagne pour l'échange ou le remboursement.",
    },
    {
        question: "Puis-je acheter en gros ou revendre vos produits ?",
        answer:
            "Bien sûr. Nous proposons des tarifs dégressifs pour les professionnels, boutiques et revendeurs. Contactez-nous sur WhatsApp pour un devis volume.",
    },
];

const FAQ = () => {
    const { data, isLoading } = useContent("faq");
    const content = data?.data || {};
    const items = content.items || FALLBACK_FAQ;

    return (
        <section id="faq" data-testid={FAQ_TID.section} className="relative py-24 md:py-32 bg-white">
            <div className="max-w-3xl mx-auto px-6 md:px-10">
                <div className="text-center">
                    <div className="dp-eyebrow">{content.eyebrow || "FAQ"}</div>
                    <h2 className="mt-3 font-display text-4xl md:text-5xl tracking-tight font-light text-neutral-900 leading-[1.05]">
                        {content.title || "Questions fréquentes."}
                    </h2>
                    <p className="mt-4 text-[16px] text-neutral-500">
                        {content.subtitle || "Tout ce qu'il faut savoir avant votre première commande."}
                    </p>
                </div>

                {isLoading && (
                    <div className="mt-16 flex justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
                    </div>
                )}

                <Accordion
                    type="single"
                    collapsible
                    className="mt-12 divide-y divide-black/[0.06] border-y border-black/[0.06]"
                >
                    {items.map((it, i) => (
                        <AccordionItem
                            key={i}
                            value={`item-${i}`}
                            data-testid={FAQ_TID.item(i)}
                            className="border-0"
                        >
                            <AccordionTrigger className="text-left font-display text-[17px] md:text-[18px] tracking-tight font-medium text-neutral-900 hover:no-underline py-6">
                                <span className="flex items-start gap-4">
                                    <span className="font-mono-tech text-[11px] text-[#d4af37] mt-1.5 shrink-0">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    {it.question}
                                </span>
                            </AccordionTrigger>
                            <AccordionContent className="text-[15px] leading-relaxed text-neutral-500 pb-6 pl-9">
                                {renderAnswer(it.answer)}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </section>
    );
};

export default FAQ;
